/**
 * Components for parallax in an SVG document.
 *
 * The parallax responds to the y-position of the child element relative
 * to the window, and optionally to the mouse.
 *
 * To use, replace <svg /> with <Parallax /> and wrap each level in the svg
 * in <Layer level={n} />. Closer levels are higher. Any level higher than 20
 * is probably too much.
 *
 * These are always hidden to screen readers.
 */

import {css, StyleSheet} from "aphrodite";
import debounce from "lodash.debounce";
import * as React from "react";

import {
    withActionScheduler,
    type ITimeout,
    type WithActionSchedulerProps,
    type WithoutActionScheduler,
} from "@khanacademy/wonder-blocks-timing";

import InView from "./in-view.ts";

type ParallaxData = {
    posX: number;
    posY: number;
    pctInView: number;
};

const ParallaxContext: React.Context<ParallaxData> = React.createContext({
    posX: 0,
    posY: 0,
    pctInView: 0,
});
ParallaxContext.displayName = "ParallaxContext";

type OwnProps = {
    children: any;
    viewBox: string;
    width?: number;
    height?: number;
    alwaysVisible?: boolean;
    className?: string;
    enableMouse?: boolean;
    preserveAspectRatio?: string;
};

type Props = OwnProps & WithActionSchedulerProps;

type State = {
    trackingMouse: number;
    mouseStartX: number;
    mouseStartY: number;
    grabStartX: number;
    grabStartY: number;
    mouseX: number;
    mouseY: number;
    grabbing: number;
};

const NORMAL_MOUSE_PARALAX = 0.1;
const GRAB_MOUSE_PARALAX = 0.8;

export class Layer extends React.Component<{
    children: any;
    level: number;
}> {
    render(): React.ReactNode {
        const {level} = this.props;
        return (
            <ParallaxContext.Consumer>
                {({posX, posY}) => {
                    return (
                        <g
                            style={{
                                transform: `translateY(${
                                    posY * level * 10
                                }px) translateX(${
                                    posX * level * 10
                                }px) translateZ(0)`,
                                willChange: "transform",
                            }}
                        >
                            {this.props.children}
                        </g>
                    );
                }}
            </ParallaxContext.Consumer>
        );
    }
}

// A layer that goes from `from` to `to` as you scroll down.
export class BreakOutLayer extends React.Component<{
    children: any;
    from: {
        x: number;
        y: number;
        scale?: number;
    };
    to: {
        x: number;
        y: number;
        scale?: number;
    };
}> {
    render(): React.ReactNode {
        const {from, to} = this.props;
        return (
            <ParallaxContext.Consumer>
                {({posY}) => {
                    // posY is between -1 and 1. We want the `from` position
                    // to happen when we're at -1, and the `to` position to
                    // happen when we're at 0. We want a linear transition
                    // between the two.
                    const percentTo = Math.min(Math.max(0, 1 - posY), 1);
                    const percentFrom = 1.0 - percentTo;
                    return (
                        <g
                            style={{
                                transform:
                                    `translateY(${
                                        percentTo * to.y + percentFrom * from.y
                                    }px)` +
                                    `translateX(${
                                        percentTo * to.x + percentFrom * from.x
                                    }px) ` +
                                    "translateZ(0) " +
                                    `scale(${
                                        percentTo * (to.scale || 1) +
                                        percentFrom * (from.scale || 1)
                                    })`,
                                transformOrigin: "center",
                                willChange: "transform",
                            }}
                        >
                            {this.props.children}
                        </g>
                    );
                }}
            </ParallaxContext.Consumer>
        );
    }
}

function clamp(num: number, min: number, max: number) {
    return Math.max(min, Math.min(max, num));
}

class Parallax extends React.Component<Props, State> {
    _trackingMouseGradiantTimeout: ITimeout | null | undefined;
    _grabbingMouseGradiantTimeout: ITimeout | null | undefined;
    state: State = {
        trackingMouse: 0,
        mouseStartX: 0,
        mouseStartY: 0,
        grabStartX: 0,
        grabStartY: 0,
        mouseX: 0,
        mouseY: 0,
        grabbing: 0,
    };

    _child = React.createRef<SVGSVGElement>();

    _startTrackingMouse: (ev: React.MouseEvent<Element>) => void = (
        ev: React.MouseEvent<Element>,
    ) => {
        const current = this._child.current;
        if (current instanceof Element) {
            const boundingRect = current.getBoundingClientRect();
            const mouseStartY =
                clamp(
                    (ev.clientY - boundingRect.top) / boundingRect.height,
                    0,
                    1,
                ) - 0.5;

            const mouseStartX =
                clamp(
                    (ev.clientX - boundingRect.left) / boundingRect.width,
                    0,
                    1,
                ) - 0.5;

            this._trackingMouseGradiantTimeout?.clear();

            this.setState({
                mouseStartX,
                mouseStartY,
                mouseX: mouseStartX,
                mouseY: mouseStartY,
                trackingMouse: 1.0,
            });
        }
    };

    _stopTrackingMouse: () => void = () => {
        this._trackingMouseGradiantTimeout?.clear();
        this._trackingMouseGradiantTimeout = this.props.schedule.timeout(() => {
            this.setState({
                trackingMouse: Math.max(
                    (this.state.trackingMouse - 0.005) * 0.98,
                    0,
                ),
            });
            if (this.state.trackingMouse > 0) {
                this._stopTrackingMouse();
            }
        }, 16);
    };
    _startMouseDown: (ev: React.MouseEvent<Element>) => void = (
        ev: React.MouseEvent<Element>,
    ) => {
        const current = this._child.current;
        if (current instanceof Element) {
            const boundingRect = current.getBoundingClientRect();
            const grabStartY =
                clamp(
                    (ev.clientY - boundingRect.top) / boundingRect.height,
                    0,
                    1,
                ) - 0.5;

            const grabStartX =
                clamp(
                    (ev.clientX - boundingRect.left) / boundingRect.width,
                    0,
                    1,
                ) - 0.5;

            this._grabbingMouseGradiantTimeout?.clear();

            this.setState({
                grabStartX,
                grabStartY,
                mouseX: grabStartX,
                mouseY: grabStartY,
                trackingMouse: 1.0,
                grabbing: 1.0,
            });
        }
    };

    _stopMouseDown: () => void = () => {
        this._grabbingMouseGradiantTimeout?.clear();
        this._grabbingMouseGradiantTimeout = this.props.schedule.timeout(() => {
            this.setState({
                grabbing: Math.max((this.state.grabbing - 0.005) * 0.98, 0),
            });
            if (this.state.grabbing > 0) {
                this._stopMouseDown();
            }
        }, 16);
    };

    _handleMouseMove: (ev: React.MouseEvent<Element>) => void = (
        ev: React.MouseEvent<Element>,
    ) => {
        this._handleMouseMoveThrottled(ev.clientX, ev.clientY);
    };

    _handleMouseMoveThrottled: (x: number, y: number) => void = debounce(
        (x: number, y: number) => {
            const current = this._child.current;
            if (current instanceof Element) {
                const boundingRect = current.getBoundingClientRect();
                const mouseY =
                    clamp((y - boundingRect.top) / boundingRect.height, 0, 1) -
                    0.5;

                const mouseX =
                    clamp((x - boundingRect.left) / boundingRect.width, 0, 1) -
                    0.5;
                this.setState({mouseX, mouseY});
            }
        },
        12,
        {leading: true, trailing: true, maxWait: 12},
    );

    render(): React.ReactNode {
        return (
            <InView>
                {({pctInView, position}) => {
                    const {
                        trackingMouse,
                        mouseStartX,
                        mouseStartY,
                        grabStartX,
                        grabStartY,
                        mouseX,
                        mouseY,
                        grabbing,
                    } = this.state;

                    const {enableMouse, alwaysVisible} = this.props;

                    const normalDeltaX =
                        trackingMouse *
                        (mouseX - mouseStartX) *
                        NORMAL_MOUSE_PARALAX;
                    const grabbingDeltaX =
                        (mouseX - grabStartX) * GRAB_MOUSE_PARALAX * grabbing;
                    const posX = clamp(normalDeltaX + grabbingDeltaX, -1, 1);

                    const normalDeltaY =
                        trackingMouse *
                        (mouseY - mouseStartY) *
                        NORMAL_MOUSE_PARALAX;
                    const grabbingDeltaY =
                        (mouseY - grabStartY) * GRAB_MOUSE_PARALAX * grabbing;
                    const posY = clamp(
                        normalDeltaY + grabbingDeltaY + position,
                        -1,
                        1,
                    );

                    return (
                        <ParallaxContext.Provider
                            value={{posX, posY, pctInView}}
                        >
                            <svg
                                aria-hidden="true"
                                fill="none"
                                height={this.props.height}
                                viewBox={this.props.viewBox}
                                onMouseEnter={
                                    enableMouse
                                        ? this._startTrackingMouse
                                        : undefined
                                }
                                onMouseLeave={
                                    enableMouse
                                        ? () => {
                                              this._stopTrackingMouse();
                                              this._stopMouseDown();
                                          }
                                        : undefined
                                }
                                onMouseMove={
                                    enableMouse
                                        ? this._handleMouseMove
                                        : undefined
                                }
                                onMouseDown={
                                    enableMouse
                                        ? this._startMouseDown
                                        : undefined
                                }
                                onMouseUp={
                                    enableMouse
                                        ? this._stopMouseDown
                                        : undefined
                                }
                                ref={this._child}
                                className={`${css(
                                    styles.svg,
                                    enableMouse && styles.grabbable,
                                    pctInView > 0 || alwaysVisible
                                        ? styles.visible
                                        : styles.hidden,
                                    grabbing === 1.0 && styles.grabbing,
                                )} ${this.props.className || ""} `}
                                preserveAspectRatio={
                                    this.props.preserveAspectRatio
                                }
                            >
                                {(pctInView > 0 || alwaysVisible) &&
                                    this.props.children}
                            </svg>
                        </ParallaxContext.Provider>
                    );
                }}
            </InView>
        );
    }
}

type ExportProps = WithoutActionScheduler<
    JSX.LibraryManagedAttributes<
        typeof Parallax,
        React.ComponentProps<typeof Parallax>
    >
>;
export default withActionScheduler(
    Parallax,
) as React.ComponentType<ExportProps>;

const styles = StyleSheet.create({
    svg: {
        overflow: "visible",
        flexShrink: 1,
    },
    grabbable: {
        cursor: "grab",
    },
    grabbing: {
        cursor: "grabbing",
    },
    visible: {
        opacity: 1.0,
        transition: "opacity 200ms ease-in-out 200ms",
    },
    hidden: {
        opacity: 0.0,
    },
});
